/**
 * Seal Subscriptions × Rebuy/Theme cart add bridge
 *
 * Ensures subscription adds include a `selling_plan` in `/cart/add.js` requests
 * when a Seal subscription selection is present on the page.
 *
 * Intentionally conservative:
 * - only modifies POSTs to /cart/add.js
 * - only when adding a single variant
 * - only when added variant matches current PDP variant (prevents leaking plan into unrelated adds)
 */
(() => {
  const CART_ADD_PATH_SUFFIX = '/cart/add.js';
  const SELLING_PLAN_FIELD_NAMES = ['selling_plan', 'selling_plan_id'];

  /** @returns {string} */
  function getCurrentVariantId() {
    try {
      const fromUrl = new URL(window.location.href).searchParams.get('variant');
      if (fromUrl && fromUrl.trim()) return fromUrl.trim();
    } catch {
      /* ignore */
    }

    const fromForm = /** @type {HTMLInputElement | null} */ (
      document.querySelector('product-form-component input[name="id"]')
    );
    if (fromForm?.value && fromForm.value.trim()) return fromForm.value.trim();

    return '';
  }

  /** @returns {string} */
  function getSelectedSellingPlanId() {
    // Prefer canonical Shopify name: selling_plan
    for (const name of SELLING_PLAN_FIELD_NAMES) {
      const el = /** @type {HTMLInputElement | HTMLSelectElement | null} */ (
        document.querySelector(`input[name="${name}"], select[name="${name}"]`)
      );
      if (!el) continue;
      if (el instanceof HTMLSelectElement) {
        const v = (el.value || '').trim();
        if (v) return v;
        continue;
      }
      const v = (el.value || '').trim();
      if (v) return v;
    }

    // Fallback scan (covers apps that namespace/rename the field)
    const candidates = /** @type {(HTMLInputElement|HTMLSelectElement)[]} */ (
      Array.from(document.querySelectorAll('input, select')).filter((node) => {
        if (!(node instanceof HTMLInputElement || node instanceof HTMLSelectElement)) return false;
        const n = (node.getAttribute('name') || '').toLowerCase();
        return (
          n === 'selling_plan' || n === 'selling_plan_id' || n.endsWith('[selling_plan]') || n.includes('selling_plan')
        );
      })
    );

    for (const node of candidates) {
      const v = (node.value || '').trim();
      if (v) return v;
    }

    return '';
  }

  /**
   * @param {unknown} value
   * @returns {string}
   */
  function asTrimmedString(value) {
    if (value == null) return '';
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number') return String(value);
    return '';
  }

  /**
   * @param {RequestInfo | URL} input
   * @returns {string}
   */
  function getUrlString(input) {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.toString();
    if (input instanceof Request) return input.url;
    return String(input);
  }

  /**
   * @param {string} urlString
   * @returns {boolean}
   */
  function isCartAddUrl(urlString) {
    try {
      const u = new URL(urlString, window.location.origin);
      return u.pathname.endsWith(CART_ADD_PATH_SUFFIX);
    } catch {
      return urlString.includes(CART_ADD_PATH_SUFFIX);
    }
  }

  /**
   * @param {HeadersInit | undefined} headers
   * @param {string} key
   * @returns {string}
   */
  function headerValue(headers, key) {
    try {
      if (!headers) return '';
      if (headers instanceof Headers) return headers.get(key) || '';
      if (Array.isArray(headers)) {
        const found = headers.find(([k]) => String(k).toLowerCase() === key.toLowerCase());
        return found ? String(found[1] ?? '') : '';
      }
      const v = /** @type {Record<string, string>} */ (headers)[key];
      if (v) return String(v);
      // try case-insensitive
      const k = Object.keys(/** @type {Record<string, string>} */ (headers)).find(
        (h) => h.toLowerCase() === key.toLowerCase()
      );
      return k ? String(/** @type {Record<string, string>} */ (headers)[k] ?? '') : '';
    } catch {
      return '';
    }
  }

  /**
   * @param {HeadersInit | undefined} headers
   * @returns {Headers}
   */
  function toHeaders(headers) {
    try {
      return new Headers(headers || undefined);
    } catch {
      return new Headers();
    }
  }

  /**
   * @param {Headers} headers
   */
  function dropContentType(headers) {
    try {
      headers.delete('content-type');
    } catch {
      /* ignore */
    }
  }

  /**
   * @param {FormData} fd
   * @returns {{ variantId: string, hasSellingPlan: boolean }}
   */
  function inspectFormData(fd) {
    const variantId = asTrimmedString(fd.get('id'));
    const hasSellingPlan = SELLING_PLAN_FIELD_NAMES.some((name) => {
      const v = asTrimmedString(fd.get(name));
      return !!v;
    });
    return { variantId, hasSellingPlan };
  }

  /**
   * @param {any} json
   * @returns {{ variantId: string, hasSellingPlan: boolean, shape: 'single'|'items'|'unknown' }}
   */
  function inspectJson(json) {
    if (json && typeof json === 'object') {
      if (Array.isArray(json.items) && json.items.length === 1) {
        const item = json.items[0] || {};
        const variantId = asTrimmedString(item.id);
        const hasSellingPlan = !!asTrimmedString(item.selling_plan || item.selling_plan_id);
        return { variantId, hasSellingPlan, shape: 'items' };
      }
      if (json.id != null) {
        const variantId = asTrimmedString(json.id);
        const hasSellingPlan = !!asTrimmedString(json.selling_plan || json.selling_plan_id);
        return { variantId, hasSellingPlan, shape: 'single' };
      }
    }
    return { variantId: '', hasSellingPlan: false, shape: 'unknown' };
  }

  /**
   * @param {any} json
   * @param {string} sellingPlanId
   * @returns {any}
   */
  function injectSellingPlanIntoJson(json, sellingPlanId) {
    if (!json || typeof json !== 'object') return json;
    if (Array.isArray(json.items) && json.items.length === 1 && json.items[0] && typeof json.items[0] === 'object') {
      const item = json.items[0];
      if (!item.selling_plan && !item.selling_plan_id) {
        item.selling_plan = sellingPlanId;
      }
      return json;
    }
    if (json.id != null) {
      if (!json.selling_plan && !json.selling_plan_id) {
        json.selling_plan = sellingPlanId;
      }
      return json;
    }
    return json;
  }

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input, init) => {
    const urlString = getUrlString(input);
    if (!isCartAddUrl(urlString)) return originalFetch(input, init);

    const method = String(init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
    if (method !== 'POST') return originalFetch(input, init);

    const sellingPlanId = getSelectedSellingPlanId();
    if (!sellingPlanId) return originalFetch(input, init);

    const currentVariantId = getCurrentVariantId();
    if (!currentVariantId) return originalFetch(input, init);

    // Prefer init.body when provided (common for theme code).
    const initBody = init?.body;
    const initHeaders = toHeaders(init?.headers);

    // Handle FormData (theme PDP add)
    if (initBody instanceof FormData) {
      const cloned = new FormData();
      for (const [k, v] of initBody.entries()) cloned.append(k, v);

      const { variantId, hasSellingPlan } = inspectFormData(cloned);
      if (!variantId || variantId !== currentVariantId || hasSellingPlan) {
        return originalFetch(input, init);
      }

      cloned.set('selling_plan', sellingPlanId);
      dropContentType(initHeaders);
      return originalFetch(input, { ...init, headers: initHeaders, body: cloned });
    }

    // Handle Request bodies (3rd party libs may pass Request directly)
    if (!initBody && input instanceof Request) {
      try {
        const reqClone = input.clone();
        const contentType = (reqClone.headers.get('content-type') || '').toLowerCase();

        if (contentType.includes('application/json')) {
          const json = await reqClone.json();
          const info = inspectJson(json);
          if (!info.variantId || info.variantId !== currentVariantId || info.hasSellingPlan) {
            return originalFetch(input, init);
          }
          const updated = injectSellingPlanIntoJson(json, sellingPlanId);
          const nextHeaders = new Headers(reqClone.headers);
          nextHeaders.set('content-type', 'application/json');
          const nextReq = new Request(reqClone.url, {
            method: reqClone.method,
            headers: nextHeaders,
            body: JSON.stringify(updated),
            credentials: reqClone.credentials,
            cache: reqClone.cache,
            redirect: reqClone.redirect,
            referrer: reqClone.referrer,
            referrerPolicy: reqClone.referrerPolicy,
            integrity: reqClone.integrity,
            keepalive: reqClone.keepalive,
            mode: reqClone.mode,
          });
          return originalFetch(nextReq);
        }

        if (contentType.includes('application/x-www-form-urlencoded') || contentType.includes('multipart/form-data')) {
          const fd = await reqClone.formData();
          const { variantId, hasSellingPlan } = inspectFormData(fd);
          if (!variantId || variantId !== currentVariantId || hasSellingPlan) {
            return originalFetch(input, init);
          }
          fd.set('selling_plan', sellingPlanId);
          const nextHeaders = new Headers(reqClone.headers);
          dropContentType(nextHeaders);
          const nextReq = new Request(reqClone.url, {
            method: reqClone.method,
            headers: nextHeaders,
            body: fd,
            credentials: reqClone.credentials,
            cache: reqClone.cache,
            redirect: reqClone.redirect,
            referrer: reqClone.referrer,
            referrerPolicy: reqClone.referrerPolicy,
            integrity: reqClone.integrity,
            keepalive: reqClone.keepalive,
            mode: reqClone.mode,
          });
          return originalFetch(nextReq);
        }
      } catch {
        // If anything goes wrong, fall through to original fetch
        return originalFetch(input, init);
      }
    }

    // Handle JSON body passed via init.body (string)
    if (typeof initBody === 'string') {
      const contentType = headerValue(init?.headers, 'content-type').toLowerCase();
      if (!contentType.includes('application/json')) {
        return originalFetch(input, init);
      }
      try {
        const json = JSON.parse(initBody);
        const info = inspectJson(json);
        if (!info.variantId || info.variantId !== currentVariantId || info.hasSellingPlan) {
          return originalFetch(input, init);
        }
        const updated = injectSellingPlanIntoJson(json, sellingPlanId);
        return originalFetch(input, { ...init, body: JSON.stringify(updated) });
      } catch {
        return originalFetch(input, init);
      }
    }

    // Handle URLSearchParams body
    if (initBody instanceof URLSearchParams) {
      const cloned = new URLSearchParams(initBody.toString());
      const variantId = asTrimmedString(cloned.get('id'));
      const hasSellingPlan = SELLING_PLAN_FIELD_NAMES.some((name) => !!asTrimmedString(cloned.get(name)));
      if (!variantId || variantId !== currentVariantId || hasSellingPlan) {
        return originalFetch(input, init);
      }
      cloned.set('selling_plan', sellingPlanId);
      return originalFetch(input, { ...init, body: cloned });
    }

    // Otherwise, don't touch unknown payload types.
    return originalFetch(input, init);
  };
})();
